import cx from 'classnames';
import truncate from 'lodash-es/truncate';
import { capFirst } from 'mk/common/text';
import ProductFacets from 'mk2/apps/bazaar/components/ProductFacets';
import ProductStateBadges, { State as AllowedState } from 'mk2/apps/bazaar/components/ProductStateBadges';
import ProductStockStatus from 'mk2/apps/bazaar/components/ProductStockStatus';
import { ProductEntity } from 'mk2/apps/bazaar/schemas';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { ConnectedStar, StarPlaceholder } from 'mk2/containers/Star/Star';
import React from 'react';
import styles from './GridItemInfo.mscss';

interface OwnProps {
    className?: string;
    product: ProductEntity;
    showStateBadges?: boolean;
}

type Props = OwnProps;

const allowedStateBadges: AllowedState[] = ['isInactive', 'isExpired', 'isDeleted'];

const GridItemInfo: React.FunctionComponent<Props> = ({ className, product, showStateBadges = true }) => {
    return (
        <div className={cx(styles.GridItemInfo, className)}>
            <div className={styles.GridItemInfo__star}>
                <ConnectedStar entity="bazaar.product" id={product.id} showCount />
            </div>
            <div className={styles.GridItemInfo__content}>
                <div className={styles.GridItemInfo__title} title={capFirst(product.title)}>
                    {capFirst(product.title)}
                    <div className={styles.GridItemInfo__mask} />
                </div>

                <div className={styles.GridItemInfo__props}>
                    {showStateBadges && (
                        <ProductStateBadges
                            className={styles.GridItemInfo__stateBadges}
                            product={product}
                            allowed={allowedStateBadges}
                        />
                    )}
                    <ProductFacets className={styles.GridItemInfo__facets} facets={product.displayProperties} small />
                    <ProductStockStatus
                        className={styles.GridItemInfo__stockStatus}
                        stockStatus={product.stockStatus}
                        small
                    />
                    <div className={styles.GridItemInfo__mask} />
                </div>

                <div className={styles.GridItemInfo__description}>
                    {truncate(product.description, { length: 200 })}
                    <div className={styles.GridItemInfo__mask} />
                </div>
            </div>
        </div>
    );
};

GridItemInfo.displayName = 'GridItemInfo';

export default React.memo<Props>(GridItemInfo);

interface PlaceholderOwnProps {
    className?: string;
}

let GridItemInfoPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({ className }) => (
    <div className={cx(styles.GridItemInfo, className)}>
        <div className={styles.GridItemInfo__star}>
            <StarPlaceholder showCount />
        </div>
        <div className={styles.GridItemInfo__content}>
            <div className={styles.GridItemInfo__title}>
                <LoadingPlaceholder width={120} />
                <div className={styles.GridItemInfo__mask} />
            </div>

            <div className={styles.GridItemInfo__props}>
                <span className={styles.GridItemInfo__stateBadges}>
                    <LoadingPlaceholder width={60} height={20} />
                </span>
                <span className={styles.GridItemInfo__facets}>
                    <LoadingPlaceholder width={40} height={20} />
                </span>
                <div className={styles.GridItemInfo__mask} />
            </div>

            <div className={styles.GridItemInfo__description}>
                <LoadingPlaceholder width="100%" />
                <div className={styles.GridItemInfo__mask} />
            </div>
        </div>
    </div>
);

GridItemInfoPlaceholder.displayName = 'GridItemInfoPlaceholder';

GridItemInfoPlaceholder = React.memo<PlaceholderOwnProps>(GridItemInfoPlaceholder);

export { GridItemInfoPlaceholder };
